<script setup>
import { defineProps } from 'vue';
// Optionales Prop, um die Trefferzahl auszublenden (z.B. in Cards)
const props = defineProps({
  hideCount: { type: Boolean, default: false },
  alwaysShowText: { type: Boolean, default: false },
  summaryObj: { type: Array, default: undefined }, // Neu: summaryObj als Prop
});
import { computed, watch } from 'vue';
import { useUserStore } from '@/stores/user';
import { useBusinessPartnersStore } from '@/stores/businessPartners';

const userStore = useUserStore();
const businessPartnersStore = useBusinessPartnersStore();

// Trefferzahl aus Summary ziehen (wie in BusinessPartnerList.vue)
const totalCount = computed(() => {
  const summary = businessPartnersStore.businessPartnerList.summary;
  if (!summary) return null;
  const match = summary.match(/(\d+[\.\d]*) Treffer/);
  return match ? match[1] : null;
});

// Aktuell angewendete Liste ("applied" Filter) anhand der Signature ermitteln
const activeList = computed(() => {
  const sig = userStore.currentSelection?.businessPartnerList?.signature;
  if (!sig) return null;
  return businessPartnersStore.businessPartnerLists.find((l) => l.signature === sig) || null;
});

// Für die Summary verwenden wir ausschließlich den zuletzt angewendeten Filter
// (aus der geladenen Liste). UI‑Änderungen im offenen Filter-Panel, die
// noch nicht angewendet wurden, sollen hier NICHT erscheinen.
const filter = computed(() => {
  if (activeList.value && activeList.value.filter) {
    return activeList.value.filter;
  }
  // Keine geladene Liste -> keine Filter-Chips anzeigen
  return {};
});
const options = computed(() => userStore.filterOption || {});

// Stelle sicher, dass der UI-Filter (BusinessPartnerFilter) immer den zuletzt
// angewendeten Listen-Filter aus dem Store widerspiegelt, damit Summary und
// Filter-Eingaben übereinstimmen – auch nach Navigation (Dashboard, Detailseite).
watch(
  () => activeList.value && activeList.value.filter,
  (listFilter) => {
    if (!listFilter) return;
    if (!userStore.currentSelection?.businessPartnerList) return;
    try {
      userStore.currentSelection.businessPartnerList.filter = JSON.parse(JSON.stringify(listFilter));
    } catch (e) {
      // Fallback: direkte Zuweisung, falls JSON-Kopie fehlschlägt
      userStore.currentSelection.businessPartnerList.filter = listFilter;
    }
  },
  { immediate: true }
);

function findById(list, id) {
  if (!Array.isArray(list)) return null;
  return list.find(o => String(o.id) === String(id)) || null;
}

// Hilfsfunktionen für kompakte Datums-/Zeitraum-Bezeichnungen
const MONTH_NAMES = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];

function toDate(val) {
  if (!val) return null;
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
}

function isSameDay(a, b) {
  return a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isFullMonthRange(a, b) {
  if (!a || !b) return false;
  if (a.getFullYear() !== b.getFullYear() || a.getMonth() !== b.getMonth()) return false;
  if (a.getDate() !== 1) return false;
  const lastDay = new Date(b.getFullYear(), b.getMonth() + 1, 0).getDate();
  return b.getDate() === lastDay;
}

function isQuarterRange(a, b) {
  if (!a || !b) return false;
  if (a.getFullYear() !== b.getFullYear()) return false;
  const startMonth = a.getMonth();
  const endMonth = b.getMonth();
  const qStartMonth = Math.floor(startMonth / 3) * 3;
  if (startMonth !== qStartMonth) return false;
  if (endMonth !== qStartMonth + 2) return false;
  if (a.getDate() !== 1) return false;
  const lastDay = new Date(b.getFullYear(), endMonth + 1, 0).getDate();
  return b.getDate() === lastDay;
}

function getISOWeekInfo(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  // Donnerstag der aktuellen Woche
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return { week: weekNo, year: d.getUTCFullYear() };
}

function isIsoWeekRange(a, b) {
  if (!a || !b) return false;
  // genau 7 Tage inkl. Start/Ende
  const diffDays = Math.round((b - a) / 86400000);
  if (diffDays !== 6) return false;
  // Start muss Montag sein (1-7, 1 = Montag, 0 = Sonntag)
  const day = a.getDay();
  return day === 1 || (day === 0 && false); // Sonntag explizit ausschließen
}

function formatDateShort(d) {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}.${mm}.${yy}`;
}

function buildPeriodLabel(dateStart, dateEnd, opts = {}) {
  const preferKw = !!opts.preferKw;
  const a = toDate(dateStart);
  const b = toDate(dateEnd);
  if (!a && !b) return '';
  if (a && !b) return `am ${formatDateShort(a)}`;
  if (!a && b) return `am ${formatDateShort(b)}`;

  // Beide vorhanden
  if (isSameDay(a, b)) {
    return `am ${formatDateShort(a)}`;
  }

  // ISO-Kalenderwoche (v.a. für Faktura interessant)
  if (preferKw && isIsoWeekRange(a, b)) {
    const { week, year } = getISOWeekInfo(a);
    return `in KW ${week}.${year}`;
  }

  // Monat
  if (isFullMonthRange(a, b)) {
    const monthName = MONTH_NAMES[a.getMonth()];
    const yy = String(a.getFullYear()).slice(-2);
    return `in ${monthName} ${yy}`;
  }

  // Quartal
  if (isQuarterRange(a, b)) {
    const q = Math.floor(a.getMonth() / 3) + 1;
    const year = a.getFullYear();
    return `im Q${q} ${year}`;
  }

  // Fallback: expliziter Bereich
  return `${formatDateShort(a)} – ${formatDateShort(b)}`;
}


const chips = computed(() => {
  // Wenn summaryObj als Prop übergeben wurde, dieses verwenden!
  const summaryObj = Array.isArray(props.summaryObj) ? props.summaryObj : Array.isArray(activeList.value?.summaryObj) ? activeList.value.summaryObj : [];
  const f = filter.value;
  const o = options.value;
  const searchCriteria = Array.isArray(activeList.value?.searchCriteria)
    ? activeList.value.searchCriteria
    : [];
  const criteriaMap = {};
  for (const c of searchCriteria) {
    if (!c || typeof c.field === 'undefined') continue;
    criteriaMap[c.field] = c.value;
  }

  const list = [];

  // 1) Bevorzugt summaryObj aus dem Backend oder Prop verwenden
  if (summaryObj.length > 0) {
    const nameMap = {
      'Segmente': 'Segment',
      'Produktfamilie': 'Produktfamilien',
      'Umsatz': 'Umsätze',
      'PLZ / Stadt': 'PLZ / Ort',
    };

    for (const entry of summaryObj) {
      if (!entry || !entry.name) continue;
      if (entry.name === 'Treffer') continue; // Treffer wird separat angezeigt
      // Treffer-Chip explizit ausblenden (auch wenn Backend ihn anders benennt)
      if (entry.name.toLowerCase().includes('treffer')) continue;
      const rawVal = entry.value;
      if (rawVal === null || rawVal === undefined) continue;
      const valStr = String(rawVal).trim();
      if (!valStr) continue;

      const rawName = String(entry.name).trim();
      const category = nameMap[rawName] || rawName;

      // Variant für Farbe je nach Kategorie bestimmen
      let variant = 'summary';
      switch (category) {
        case 'Kontaktstatus':
          variant = 'contact';
          break;
        case 'Kundenstatus':
          variant = 'customer';
          break;
        case 'Umsätze':
          variant = 'sales';
          break;
        case 'Produkte inklusive':
        case 'Produkte exklusive':
        case 'Produkte ausgeschlossen':
        case 'Produkte':
          variant = 'product';
          break;
        case 'Produktfamilien':
        case 'Produktfamilie inklusive':
        case 'Produktfamilie exklusive':
        case 'Produktfamilien inklusive':
        case 'Produktfamilien exklusive':
        case 'Produktfamilie ausgeschlossen':
        case 'Produktfamilien ausgeschlossen':
          variant = 'segment';
          break;
        case 'Segment':
          variant = 'segments';
          break;
        case 'Potential':
          variant = 'potential';
          break;
        case 'PLZ':
        case 'PLZ / Stadt':
        case 'Ort':
          variant = 'cityZip';
          break;
        case 'Branche':
          variant = 'sector';
          break;
        case 'Faktura':
          variant = 'invoice';
          break;
        case 'Sortierung':
          variant = 'sort';
          break;
        default:
          variant = 'summary';
      }

      // Für Produkte / Produktfamilien Werte als einzelne Pills rendern,
      // wenn das Backend sie mit " | " trennt
      let items = null;
      let groupMode = null;
      let groupModeLabel = '';
      let normalizedCategory = category;
      if (variant === 'product' || variant === 'segment') {
        const parts = valStr.split('|').map(s => s.trim()).filter(Boolean);
        if (parts.length > 1) {
          // Für summaryObj: einzelne Werte als Pills ohne (inkl.)/(exkl.)
          items = parts.map((name, idx) => ({
            key: `summary-${entry.order ?? category}-${idx}`,
            name,
          }));
        }

        const lowerCat = category.toLowerCase();
        if (lowerCat.includes('inklusive')) {
          groupMode = 'include';
          groupModeLabel = 'inklusive';
        } else if (lowerCat.includes('exklusive') || lowerCat.includes('ausgeschlossen')) {
          groupMode = 'exclude';
          groupModeLabel = 'ausgeschlossen';
        }

        if (groupMode) {
          normalizedCategory = category
            .replace(/ inklusive/i, '')
            .replace(/ exklusive/i, '')
            .replace(/ ausgeschlossen/i, '')
            .trim();
        }
      }

      const chip = {
        key: `summary-${entry.order ?? category}`,
        category,
        text: valStr,
        variant,
      };
      if (items) chip.items = items;
      if (groupMode) {
        chip.groupMode = groupMode;
        chip.groupModeLabel = groupModeLabel;
        chip.normalizedCategory = normalizedCategory;
      }
      list.push(chip);
    }

    return list;
  }

  // Kontaktstatus (SalesPitch-Status) inkl. Kontaktzeitraum in EINEM Chip
  if (
    f.businessPartnerSalesPitchStatus &&
    f.businessPartnerSalesPitchStatus.id !== null &&
    f.businessPartnerSalesPitchStatus.id !== undefined &&
    String(f.businessPartnerSalesPitchStatus.id) !== '-1'
  ) {
    const opt = findById(
      o.businessPartnerSalesPitchStatus || o.SalesPitchStatus || o.salesPitchStatus,
      f.businessPartnerSalesPitchStatus.id
    );
    const baseLabel = opt?.listAs || opt?.name;
    const { dateStart, dateEnd } = f.businessPartnerSalesPitchStatus;
    const range = buildPeriodLabel(dateStart, dateEnd, { preferKw: false });
    const text = baseLabel || '';

    if (text || range) {
      list.push({
        key: 'contactStatus',
        category: 'Kontaktstatus',
        text,
        rangeLabel: range || '',
        variant: 'contact',
      });
    }
  }

  // Kundenstatus (Kundenstatus-Dropdown)
  if (f.idBusinessPartnerStatus !== null && f.idBusinessPartnerStatus !== undefined && f.idBusinessPartnerStatus !== -1) {
    const opt = findById(o.businessPartnerStatus || o.status, f.idBusinessPartnerStatus);
    const label = opt?.listAs || opt?.name;
    if (label) {
      list.push({
        key: 'customerStatus',
        category: 'Kundenstatus',
        text: label,
        variant: 'customer',
      });
    }
  }

  // Produktfamilie (Segmente) – als ein gemeinsamer Chip mit farbigen (inkl./exkl.) Badges
  if (Array.isArray(f.idBusinessPartnerProductSegment) && f.idBusinessPartnerProductSegment.length > 0) {
    const includeMap = f.produktKombiInclude || {};
    const excludeMap = f.produktKombiExclude || {};
    const allSeg = o.businessPartnerProductSegment || [];
    const items = [];

    for (const raw of f.idBusinessPartnerProductSegment) {
      const isObj = typeof raw === 'object' && raw !== null;
      const id = isObj ? raw.id : raw;
      const opt = findById(allSeg, id);
      const name = opt?.name || id;
      // Backend-Filter können bereits das Format { id, include } haben.
      // Bevorzugt dieses Flag nutzen; sonst auf die Include/Exclude-Maps
      // aus dem UI-Filter zurückfallen.
      let included = false;
      let excluded = false;
      if (isObj && Object.prototype.hasOwnProperty.call(raw, 'include')) {
        included = !!raw.include;
        excluded = !raw.include;
      } else {
        included = !!includeMap[id] && !excludeMap[id];
        excluded = !!excludeMap[id];
      }
      let modeLabel = '';
      let mode = 'none';
      if (included) {
        modeLabel = 'inkl.';
        mode = 'include';
      } else if (excluded) {
        modeLabel = 'exkl.';
        mode = 'exclude';
      }
      items.push({
        key: `segment-${id}`,
        name,
        mode,
        modeLabel,
      });
    }

    if (items.length > 0) {
      list.push({
        key: 'segment-group',
        category: 'Produktfamilien',
        items,
        variant: 'segment',
      });
    }
  }

  // Produkte – ebenfalls gruppiert in einem Chip
  if (Array.isArray(f.idBusinessPartnerProduct) && f.idBusinessPartnerProduct.length > 0) {
    const includeMap = f.produkteInclude || {};
    const excludeMap = f.produkteExclude || {};
    const allProd = (o.businessPartnerProduct || []).filter(p => p.useInBusinessPartnerFilter);
    const items = [];

    for (const raw of f.idBusinessPartnerProduct) {
      const isObj = typeof raw === 'object' && raw !== null;
      const id = isObj ? raw.id : raw;
      const opt = findById(allProd, id);
      const name = opt?.name || id;
      let included = false;
      let excluded = false;
      if (isObj && Object.prototype.hasOwnProperty.call(raw, 'include')) {
        included = !!raw.include;
        excluded = !raw.include;
      } else {
        included = !!includeMap[id] && !excludeMap[id];
        excluded = !!excludeMap[id];
      }
      let modeLabel = '';
      let mode = 'none';
      if (included) {
        modeLabel = 'inkl.';
        mode = 'include';
      } else if (excluded) {
        modeLabel = 'exkl.';
        mode = 'exclude';
      }
      items.push({
        key: `product-${id}`,
        name,
        mode,
        modeLabel,
      });
    }

    if (items.length > 0) {
      list.push({
        key: 'product-group',
        category: 'Produkt',
        items,
        variant: 'product',
      });
    }
  }

  // Segmente (Kundenlisten)
  if (f.idBusinessPartnerList !== undefined && f.idBusinessPartnerList !== null && f.idBusinessPartnerList !== -1) {
    const opt = findById(o.businessPartnerLists, f.idBusinessPartnerList);
    const label = opt?.name;
    if (label) {
      list.push({
        key: 'segments',
        category: 'Segment',
        text: label,
        variant: 'segments',
      });
    }
  }

  // Potential
  if (f.idBusinessPartnerPotential && f.idBusinessPartnerPotential !== -1) {
    const opt = findById(o.businessPartnerPotential, f.idBusinessPartnerPotential);
    if (opt && opt.name) {
      list.push({
        key: 'potential',
        category: 'Potential',
        text: opt.name,
        variant: 'potential',
      });
    }
  }

  // PLZ / Stadt
  const cityZipId =
    (typeof f.idBusinessPartnerCityZip !== 'undefined' && f.idBusinessPartnerCityZip !== null)
      ? f.idBusinessPartnerCityZip
      : criteriaMap.idBusinessPartnerCityZip;
  if (cityZipId && cityZipId !== -1) {
    const opt = findById(o.businessPartnerCityZips, cityZipId);
    if (opt && opt.name) {
      list.push({
        key: 'cityZip',
        category: 'PLZ / Stadt',
        text: opt.name,
        variant: 'cityZip',
      });
    }
  }

  // Branche
  const rawSector =
    (typeof f.nameBusinessPartnerSector !== 'undefined' && f.nameBusinessPartnerSector !== null && f.nameBusinessPartnerSector !== '')
      ? f.nameBusinessPartnerSector
      : criteriaMap.nameBusinessPartnerSector;
  const sectorLabel =
    rawSector && typeof rawSector === 'object'
      ? (rawSector.name || rawSector.label || '')
      : rawSector;
  if (sectorLabel) {
    list.push({
      key: 'sector',
      category: 'Branche',
      text: sectorLabel,
      variant: 'sector',
    });
  }

  // Suche (integrierte BusinessPartner-Suche)
  if (f.search && typeof f.search === 'object' && typeof f.search.string === 'string') {
    const term = f.search.string.trim();
    if (term) {
      const mode = (f.search.mode || 'name');
      const wwRaw = f.search.wholeWord;
      const wholeWord = (wwRaw === 1 || wwRaw === '1' || wwRaw === true || wwRaw === 'true' || wwRaw === 'True');

      let innerLabel = '';
      if (mode === 'GPNo') {
        innerLabel = `GP-Nr. = "${term}"`;
      } else if (mode === 'tel') {
        innerLabel = `Tel. enthält "${term}"`;
      } else {
        // name / default
        innerLabel = wholeWord ? `Name ist "${term}"` : `Name enthält "${term}"`;
      }

      const text = innerLabel;

      list.push({
        key: 'search',
        category: 'Suche',
        text,
        variant: 'search',
        wholeWordLabel: wholeWord ? 'Ganzes Wort' : '',
      });
    }
  }

  // Kontaktzeitraum wird im Kontaktstatus-Chip mit angezeigt (s.o.)

  // Faktura-Zeitraum
  if (f.invoice && (f.invoice.dateStart || f.invoice.dateEnd)) {
    const { dateStart, dateEnd } = f.invoice;
    const rangeLabel = buildPeriodLabel(dateStart, dateEnd, { preferKw: true });
    if (rangeLabel) {
      list.push({
        key: 'invoiceRange',
        category: 'Faktura',
        text: rangeLabel,
        variant: 'invoice',
      });
    }
  }

  // Umsatz (Produktgruppen)
  if (f.salesValues && typeof f.salesValues === 'object') {
    const labels = {
      total: 'Gesamt',
      gs: 'Gelbe Seiten',
      tb: 'Telefonbuch',
      oe: 'Örtliche',
      omp: 'OMP',
    };
    const parts = [];

    for (const [key, label] of Object.entries(labels)) {
      const sv = f.salesValues[key];
      if (!sv) continue; // "Beliebig" -> kein Chip-Eintrag

      let text = '';
      if (sv.min === 0 && sv.max === 0) {
        text = `${label}: kein Umsatz`;
      } else {
        const from = typeof sv.min === 'number' ? sv.min : null;
        const to = typeof sv.max === 'number' ? sv.max : null;
        const fmt = (v) => v.toLocaleString('de-DE');
        if (from !== null && to !== null) text = `${label}: ${fmt(from)}–${fmt(to)} €`;
        else if (from !== null) text = `${label}: ≥ ${fmt(from)} €`;
        else if (to !== null) text = `${label}: ≤ ${fmt(to)} €`;
      }

      if (text) parts.push(text);
    }

    if (parts.length > 0) {
      list.push({
        key: 'revenue',
        category: 'Umsätze',
        text: parts.join(' · '),
        variant: 'sales',
      });
    }
  }

  // Sortierung (auch Default-Sortierung anzeigen)
  if (f.idBusinessPartnerOrder !== undefined && f.idBusinessPartnerOrder !== null) {
    const opt = findById(o.businessPartnerOrder, f.idBusinessPartnerOrder);
    // Wenn kein expliziter Sortier-Eintrag gefunden wird (z.B. id -1), Standard-Label "Datum" verwenden
    const label = opt?.name || 'Datum';
    const order = (f.order || 'DESC').toUpperCase();
    const orderLabel = order === 'ASC' ? 'aufsteigend' : 'absteigend';
    list.push({
      key: 'sort',
      category: 'Sortierung',
      text: label,
      orderLabel,
      variant: 'sort',
    });
  }

  return list;
});

// Aktive Summary-Sektionen (für Filter-UI) in den User-Store spiegeln
watch(
  chips,
  (currentChips) => {
    const sections = {
      contactStatus: false,
      customerStatus: false,
      segments: false,
      produkte: false,
      productGroups: false,
      potential: false,
      cityZip: false,
      sector: false,
      invoice: false,
      sort: false,
      search: false,
      lists: false,
    };

    for (const chip of currentChips || []) {
      switch (chip.variant) {
        case 'contact':
          sections.contactStatus = true;
          break;
        case 'customer':
          // Kundenstatus-Sektion wie andere Filter-Sektionen behandeln:
          // Sobald ein Kundenstatus-Chip existiert, Sektion als aktiv markieren.
          sections.customerStatus = true;
          break;
        case 'segment':
          // Produktfamilien (Segmente im Sinne von Produktgruppen)
          sections.lists = true;
          break;
        case 'segments':
          // Kundenlisten / BusinessPartner-Listen
          sections.segments = true;
          break;
        case 'product':
          sections.produkte = true;
          break;
        case 'sales':
          sections.productGroups = true;
          break;
        case 'potential':
          sections.potential = true;
          break;
        case 'cityZip':
          sections.cityZip = true;
          break;
        case 'sector':
          sections.sector = true;
          break;
        case 'invoice':
          sections.invoice = true;
          break;
        case 'sort':
          sections.sort = true;
          break;
        case 'search':
          sections.search = true;
          break;
      }
    }

    if (userStore.currentSelection?.businessPartnerList) {
      userStore.currentSelection.businessPartnerList.activeSummarySections = sections;
    }
  },
  { immediate: true, deep: true }
);

function getFilterSectionKeyFromVariant(variant) {
  switch (variant) {
    case 'contact': return 'contact';
    case 'customer': return 'customer';
    case 'segment': return 'segment';
    case 'product': return 'product';
    case 'segments': return 'segments';
    case 'sales': return 'sales';
    case 'potential': return 'potential';
    case 'cityZip': return 'cityZip';
    case 'sector': return 'sector';
    case 'invoice': return 'invoice';
    case 'sort': return 'sort';
    case 'search': return 'search';
    default: return null;
  }
}

function handleChipClick(chip) {
  const section = getFilterSectionKeyFromVariant(chip.variant);
  if (!section) return;
  // Filter sichtbar machen
  if (userStore.currentSelection?.businessPartnerList?.filter) {
    userStore.currentSelection.businessPartnerList.filter.show = true;
  }
  // Ziel-Section für den Filter setzen; BusinessPartnerFilter scrollt darauf
  userStore.currentSelection.businessPartnerList.scrollToFilterSection = section;
}
</script>

<template>
  <div class="filter-summary-wrapper" v-if="(!props.hideCount && totalCount) || chips.length">
    <div class="summary-count" v-if="totalCount && !props.hideCount">
      <span class="count-number">{{ totalCount }}</span>
      <span class="count-label">Treffer</span>
    </div>
    <div class="summary-chips" v-if="chips.length">
      <div
        v-for="chip in chips"
        :key="chip.key"
        class="chip"
        :class="`chip--${chip.variant}`"
        @click="handleChipClick(chip)"
      >
        <span class="chip-accent"></span>
        <template v-if="props.alwaysShowText || userStore.currentSelection?.businessPartnerList?.filter?.isOpen">
          <span class="chip-category">
            <template v-if="chip.groupModeLabel">
              {{ chip.normalizedCategory || chip.category }}
              <span
                class="chip-item-mode"
                :class="{
                  'chip-item-mode--include': chip.groupMode === 'include',
                  'chip-item-mode--exclude': chip.groupMode === 'exclude',
                }"
              >
                {{ chip.groupModeLabel }}
              </span>:
            </template>
            <template v-else>
              {{ chip.category }}:
            </template>
          </span>
          <span class="chip-text">
            <!-- Gruppierte Einträge (z.B. Produktfamilie, Produkte) -->
            <template v-if="chip.items && chip.items.length">
              <span
                v-for="(item, idx) in chip.items"
                :key="item.key || idx"
                class="chip-item"
              >
                <span
                  class="chip-item-name"
                  :class="{ 'chip-item-pill': !item.modeLabel }"
                >
                  {{ item.name }}
                </span>
                <span
                  v-if="item.modeLabel"
                  class="chip-item-mode"
                  :class="{
                    'chip-item-mode--include': item.mode === 'include',
                    'chip-item-mode--exclude': item.mode === 'exclude',
                  }"
                >
                  {{ item.modeLabel }}
                </span>
                <span
                  v-if="idx < chip.items.length - 1 && item.modeLabel"
                  class="chip-separator"
                >
                  ,
                </span>
              </span>
            </template>
            <!-- Einfache Text-Chips -->
            <template v-else>
              <!-- Spezielle Darstellung für Sortierung: Richtung als neutrale Bubble -->
              <template v-if="chip.variant === 'sort'">
                <span class="chip-item-name">{{ chip.text }}</span>
                <span
                  v-if="chip.orderLabel"
                  class="chip-item-mode chip-item-mode--neutral"
                >
                  {{ chip.orderLabel }}
                </span>
              </template>
              <!-- Suche: optionales "Ganzes Wort" als Bubble -->
              <template v-else-if="chip.variant === 'search'">
                <span class="chip-item-name">{{ chip.text }}</span>
                <span
                  v-if="chip.wholeWordLabel"
                  class="chip-item-mode chip-item-mode--neutral"
                >
                  {{ chip.wholeWordLabel }}
                </span>
              </template>
              <!-- Kontaktstatus: Zeitraum als eigene Bubble -->
              <template v-else-if="chip.variant === 'contact'">
                <span v-if="chip.text" class="chip-item-name">{{ chip.text }}</span>
                <span
                  v-if="chip.rangeLabel"
                  class="chip-item-mode chip-item-mode--neutral"
                >
                  {{ chip.rangeLabel }}
                </span>
              </template>
              <template v-else>
                {{ chip.text }}
              </template>
            </template>
          </span>
        </template>
        <template v-else>
          <!-- Chips immer sichtbar, aber ohne Text -->
          <span class="chip-placeholder" style="min-width:1.5em;min-height:1em;display:inline-block;"></span>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.filter-summary-wrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.4rem;
}
.summary-count {
  display: inline-flex;
  align-items: baseline;
  gap: 0.25rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: #111827;
}
.count-number {
  font-size: 1.05rem;
}
.count-label {
  font-size: 0.85rem;
  color: #6b7280;
}
.summary-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}
/* Kleinere Chips für Card-Ansicht */
.chip {
  display: inline-flex;
  align-items: center;
  gap: 0.18rem;
  padding: 0.07rem 0.28rem 0.07rem 0.22rem;
  border-radius: 7px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  font-size: 0.68rem;
  color: #374151;
  cursor: pointer;
  min-height: 1.1em;
}
.chip-accent {
  width: 0.18rem;
  height: 0.9rem;
  border-radius: 999px;
  background: #9ca3af;
}
.chip-category {
  font-weight: 600;
}
.chip-text {
  white-space: nowrap;
}

.chip-item-name {
  white-space: nowrap;
}

.chip-item-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.02rem 0.4rem;
  border-radius: 999px;
  background: rgba(var(--chip-accent-rgb), 0.18);
  border: 1px solid rgba(var(--chip-accent-rgb), 0.5);
  font-size: 0.76rem;
  margin-right: 0.2rem;
}

.chip-item-mode {
  margin-left: 0.2rem;
  font-size: 0.76rem;
  border-radius: 999px;
  padding: 0.04rem 0.35rem;
  font-weight: 500;
}

/* Im Kategorienamen (Produkte inklusive/ausgeschlossen) soll die Pill
   die gleiche Schriftgröße wie der Text davor haben */
.chip-category .chip-item-mode {
  font-size: .85rem;
  padding-inline: .5rem;
}

.chip-item-mode--include {
  color: #166534;
  background: rgba(22, 163, 74, 0.12);
  border: 1px solid rgba(22, 163, 74, 0.35);
  font-weight: 600;
}

.chip-item-mode--exclude {
  color: #b91c1c;
  background: rgba(225, 29, 72, 0.12);
  border: 1px solid rgba(225, 29, 72, 0.35);
  font-weight: 600;
}

.chip-item-mode--neutral {
  color: #111827;
  background: rgba(148, 163, 184, 0.18);
  border: 1px solid rgba(148, 163, 184, 0.5);
}

.chip-separator {
  margin: 0 0.12rem 0 0.08rem;
}

/* Farbvarianten – definieren nur die RGB-Akzentfarbe */
.chip--contact { --chip-accent-rgb: 37, 99, 235; }
.chip--customer { --chip-accent-rgb: 45, 212, 191; }
.chip--segment,
.chip--segmentInclude { --chip-accent-rgb: 236, 72, 153; }
.chip--segmentExclude { --chip-accent-rgb: 219, 39, 119; }
.chip--product,
.chip--productInclude { --chip-accent-rgb: 249, 115, 22; }
.chip--productExclude { --chip-accent-rgb: 234, 88, 12; }
.chip--potential { --chip-accent-rgb: 6, 182, 212; }
.chip--cityZip { --chip-accent-rgb: 14, 165, 233; }
.chip--sector { --chip-accent-rgb: 100, 116, 139; }
.chip--segments { --chip-accent-rgb: 245, 158, 11; }
.chip--salesPitch { --chip-accent-rgb: 250, 204, 21; }
.chip--invoice { --chip-accent-rgb: 225, 29, 72; }
.chip--sales { --chip-accent-rgb: 34, 197, 94; }
.chip--sort { --chip-accent-rgb: 107, 33, 168; }
.chip--summary { --chip-accent-rgb: 148, 163, 184; }

/* Gemeinsame Verwendung der Akzentfarbe für Hintergrund, Rahmen und Balken
   – Opacity an die Filter-Headlines angelehnt */
.chip[class*='chip--'] {
  background: rgba(var(--chip-accent-rgb), 0.12);
  border-color: rgba(var(--chip-accent-rgb), 0.35);
}

.chip[class*='chip--'] .chip-accent {
  background: rgb(var(--chip-accent-rgb));
}
</style>
